import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination
} from '@mui/material';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import { AxiosResponse } from 'axios';
import ButtonIcon from 'components/ButtonIcon';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import GenericDialog from 'components/GenericDialog';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import UseAuthContext from 'context/AuthContext';
import { debounce } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getOrganizationLists, getOrganizationListsCSV } from 'services/admin-organizations';
import { axiosInstance } from 'utils/AxiosInstance';
import { API_PATHS } from 'utils/constants';
import { IErrorResponse } from 'utils/contracts';
import {
  formatNumber,
  getErrorHtmlContent
} from 'utils/utils';
import OrganizationFilter from './OrganizationFilter';

const TableRowWithHover = ({ index, item, handleViewBusinessPage, setOrganization, navigate, data, t, setAnchorElMenu, setMenuOrgId, menuOrgId, anchorElMenu, handleCloseMenu, deleteOrganizationConfirmation }) => {
  const [hover, setHover] = useState(false); // New state for hover

  return (
    <TableRow
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        cursor: 'pointer',
      }}
      key={item.id}
    >
      <CustomTableCell onClick={(e) => handleViewBusinessPage(e, item)} width={"60px"}>
        {item.shortId}
      </CustomTableCell>
      <CustomTableCell onClick={(e) => handleViewBusinessPage(e, item)} style={{ width: "250px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,

          }}
        >
          {item.image_url ? (
            <img src={item.image_url} width="30px" />
          ) : null}
          {item.company_name}
        </Box>
      </CustomTableCell>
      <CustomTableCell onClick={(e) => handleViewBusinessPage(e, item)} style={{ width: "230px" }}>
        <span className="data">
          {formatNumber(item.totalCredits || 0)}
        </span>
      </CustomTableCell>
      <CustomTableCell onClick={(e) => handleViewBusinessPage(e, item)} style={{ width: "230px" }}>
        <span className="data">
          {formatNumber(item.totalPoints || 0)}
        </span>
      </CustomTableCell>

      <CustomTableCell onClick={(e) => handleViewBusinessPage(e, item)} style={{ width: "230px" }}>
        {formatNumber(item.projectCount)}
      </CustomTableCell>
      <CustomTableCell onClick={(e) => handleViewBusinessPage(e, item)} style={{ width: "230px" }}>
        {formatNumber(item.revisionCount)}
      </CustomTableCell>
      <CustomTableCell width={"100px"} onClick={(e) => handleViewBusinessPage(e, item)}>
        <div style={{ display: "flex", gap: "10px" }}>
          <span className="data">
            {moment
              .utc(item.updatedAt)
              .local()
              .format("DD/MM/YY")}
          </span>
          <span className="time" >
            {moment.utc(item.updatedAt).local().format("HH:mm")}
          </span>
        </div>
      </CustomTableCell>
      <CustomTableCell width={"100px"} align="right">
        <ButtonIcon
          isActive={menuOrgId === item.id}
          aria-label='more'
          aria-controls='long-menu'
          aria-haspopup='true'
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setAnchorElMenu(event.currentTarget);
            setMenuOrgId(item.id);
          }}
        >
          <MoreHorizIcon fontSize='medium' />
        </ButtonIcon>
        <Menu
          id={`menu-${item.id}`}
          open={menuOrgId === item.id}

          anchorEl={anchorElMenu}
          onClose={handleCloseMenu}
          sx={{ fontSize: "14px" }}
        >
          <div>
            <MenuItem onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/edit/business/${item.id}`);
            }}>{t('BUSINESS_MENU_EDIT')}</MenuItem>
            <MenuItem onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOrganization(
                data.find((x: any) => x.id == item.id)
              );
              navigate(`/admin/business/${item.id}/projects`);
            }}>{t('BUSINESS_MENU_VIEW')}</MenuItem>
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteOrganizationConfirmation(item);
              }}>{t('BUSINESS_MENU_DELETE')}</MenuItem></div>
        </Menu>
      </CustomTableCell>
    </TableRow>
  );
};

const AdminDashboard = () => {
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('updatedAt');
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [openDeletePopUp, setOpenDeletePopUp] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [menuOrgId, setMenuOrgId] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<any>([]);
  const { setOrganization, setOrganizationId } = React.useContext(UseAuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('business');
  const [loadingDownloadCSV, setLoadingDownloadCSV] = useState(false);
  const {
    data: organizationsData,
    isLoading,
  } = useQuery(
    ['GetOrganizations', page, pageSize, sortOrder, sortBy, searchValue, searchType],
    () => getOrganizationLists({
      page,
      pageSize,
      sortOrder,
      sortBy,
      search: searchValue || null,
      searchType: searchType || null
    }),
    {
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (organizationsData?.paging.total > page * pageSize) {
      const nextPage = page + 1;
      queryClient.prefetchQuery(
        ['GetOrganizations', nextPage, pageSize, sortOrder, sortBy, searchValue, searchType],
        () => getOrganizationLists({
          page: nextPage,
          pageSize,
          sortOrder,
          sortBy,
          search: searchValue || null,
          searchType: searchType || null
        })
      );
    }
  }, [page, organizationsData, searchType]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newOrderBy);
    setPage(1);
  };

  const debouncedSearch = debounce((value: string, searchType: string) => {
    if (value.trim() === '') {
      setSearchValue('');
      setSearchType('business'); // Default to business search type
      setPage(1);
      return;
    }
    // Only allow supported search types
    if (searchType === 'business' || searchType === 'organizationId') {
      setSearchValue(value);
      setSearchType(searchType);
      setPage(1);
    }
  }, 300);

  const deleteMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const response = await axiosInstance.delete<any, AxiosResponse<any>>(
        `${API_PATHS.ADMIN_DELETE_ORGANIZATION}/${orgId}`
      );
      return response;
    },
    onSuccess() {
      setOpenDeletePopUp(false);
      queryClient.invalidateQueries(['GetOrganizations']);
      enqueueSnackbar(t('DELETE_ORGANIZATION_MESSAGE_SUCCESS'), {
        variant: 'success',
      });
    },
    onError(error: IErrorResponse, variables, context) {
      setOpenDeletePopUp(false);
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
    },
  });

  const deleteOrganizationHandler = async (orgID: any) => {
    try {
      await deleteMutation.mutateAsync(orgID);
    } catch (error: any) { }
  };

  const deleteOrganizationConfirmation = (org: any) => {
    setAnchorEl(org.id);
    setBusinessName(org.company_name)
    setOpenDeletePopUp(true);

  };

  const handleViewBusinessPage = (e: React.MouseEvent<unknown>, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedOrg = organizationsData.data.find((x: any) => x.id == item.id);
    setOrganization(selectedOrg);
    setOrganizationId(item.id);

    // Redirect to inspections for inspection organizations, otherwise to projects
    if (selectedOrg?.isInspectionType) {
      navigate(`/admin/inspections/${item.id}`);
    } else {
      navigate(`/admin/business/${item.id}/projects`);
    }
  }

  const handleClose = () => {
    setOpenDeletePopUp(false);
    setAnchorEl(null);
  };

  const handleCloseMenu = () => {
    setAnchorElMenu(null);
    setMenuOrgId(null);
  };

  const handleDownloadBusinessData = async () => {
    setLoadingDownloadCSV(true);

    try {
      const response = await getOrganizationListsCSV();
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `business_data_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setLoadingDownloadCSV(false);
    } catch (error) {
      setLoadingDownloadCSV(false);
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    }
  };

  return (
    <Box>
      <OrganizationFilter
        title={t('ADMIN_DASHBOARD_TITLE')}
        isCreateBusiness={true}
        isLoading={isLoading}
        handleSearch={debouncedSearch}
        handleDownloadBusinessData={handleDownloadBusinessData}
        loadingDownloadCSV={loadingDownloadCSV}
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
              <CustomTableFontCell
                sortDirection={sortBy === "index" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "shortId"}
                  direction={sortBy === "shortId" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "shortId")}
                >
                  {t('REVISION_INDEX_COLUMN')}
                  {sortBy === "shortId" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "company_name" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "company_name"}
                  direction={sortBy === "company_name" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "company_name")}
                >
                  {t("ADMIN_DASHBOARD_NAME")}
                  {sortBy === "company_name" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "credit" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "totalCredits"}
                  direction={sortBy === "totalCredits" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "totalCredits")}
                >
                  {t("ADMIN_DASHBOARD_CREDITS")}
                  {sortBy === "totalCredits" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "totalPoints" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "totalPoints"}
                  direction={sortBy === "totalPoints" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "totalPoints")}
                >
                  {t("ADMIN_DASHBOARD_PURCHASED")}
                  {sortBy === "totalPoints" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "projectCount" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "projectCount"}
                  direction={sortBy === "projectCount" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "projectCount")}
                >
                  {t("ADMIN_DASHBOARD_TOTAL_PROJECTS")}
                  {sortBy === "projectCount" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "revisionCount" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "revisionCount"}
                  direction={sortBy === "revisionCount" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "revisionCount")}
                >
                  {t("ADMIN_DASHBOARD_TOTAL_REVISIONS")}
                  {sortBy === "revisionCount" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "updatedAt" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "updatedAt")}
                >
                  {t("ADMIN_DASHBOARD_DATE")}
                  {sortBy === "updatedAt" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell align="right"></CustomTableFontCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizationsData?.data.map((item, index) => (
              <TableRowWithHover
                key={item.id}
                index={(page - 1) * pageSize + index + 1}
                item={item}
                handleViewBusinessPage={handleViewBusinessPage}
                setOrganization={setOrganization}
                navigate={navigate}
                data={organizationsData.data}
                t={t}
                setAnchorElMenu={setAnchorElMenu}
                setMenuOrgId={setMenuOrgId}
                menuOrgId={menuOrgId}
                anchorElMenu={anchorElMenu}
                handleCloseMenu={handleCloseMenu}
                deleteOrganizationConfirmation={deleteOrganizationConfirmation}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={organizationsData?.paging.total || 0}
        rowsPerPageOptions={[20]}
        rowsPerPage={pageSize}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}

        labelDisplayedRows={({ from, to, count }) =>
          t('MUI_PAGINATION_RANGE', {
            from: from,
            to: Math.min(to, count),
            count
          })}
      />
      <GenericDialog
        open={openDeletePopUp}
        onClose={handleClose}
        title={`${businessName}`}
        onConfirm={(e) => deleteOrganizationHandler(anchorEl)}
        confirmButtonText={t("DELETE_BUTTON")}
        cancelButtonText={t("CANCEL_BUTTON")}
        isLoading={deleteMutation.isLoading}
      >
        {t("ORGANISATION_DELETE_POPUP_DESCRIPTION")}
      </GenericDialog>
    </Box>
  );
};

export default AdminDashboard;